import type { ReactNode } from "react";
import { useState } from "react";
import { Button, KeyValue, Modal, type KeyValueItem } from "@/ui";
import { ConfirmNote } from "./ConfirmNote";
import type { EntityFormState } from "./EntityDrawer";
import { FormDrawer } from "./FormDrawer";
import type { Errors } from "./useDraft";
import { useDraft } from "./useDraft";
import { useSave, type SaveOptions } from "./useSave";

export interface AppendDrawerProps<D extends object, In, Out> extends Omit<SaveOptions<Out>, "onSaved"> {
  /** 实体名，用在标题与按钮上："新增售价"、"确认新增售价"。 */
  noun: string;
  initial: D;
  convert: (draft: D) => { body?: In; errors: Errors<D> };
  save: (body: In) => Promise<Out>;
  /** 确认弹窗顶部的说明：这次新增会带来什么。 */
  note: ReactNode;
  /** 确认弹窗里复述要写入的内容。 */
  summary: (body: In) => KeyValueItem[];
  onClose: () => void;
  children: (form: EntityFormState<D>) => ReactNode;
}

/** 新增价格时段和汇率：校验后复述写入内容，二次确认才 POST（docs/03 3.7、3.8，D113）。 */
export function AppendDrawer<D extends object, In, Out>({ noun, initial, convert, save, note, summary, invalidate, message, onClose, children }: AppendDrawerProps<D, In, Out>) {
  const form = useDraft(initial);
  const [pending, setPending] = useState<In | null>(null);
  const m = useSave(save, { invalidate, message, onSaved: onClose });
  const confirm = () => {
    const { body, errors } = convert(form.draft);
    form.setErrors(errors);
    if (body) setPending(body);
  };
  return (
    <>
      <FormDrawer open title={`新增${noun}`} submitLabel={`新增${noun}`} saving={m.saving} error={m.error} warning={m.warning} onSubmit={confirm} onClose={onClose}>
        {children({ draft: form.draft, errors: form.errors, set: form.set })}
      </FormDrawer>
      {pending && (
        <Modal open title={`确认新增${noun}`} onClose={() => setPending(null)}
          footer={<><Button onClick={() => setPending(null)}>返回修改</Button><Button variant="primary" disabled={m.saving} onClick={() => { m.reset(); m.save(pending, { onSettled: () => setPending(null) }); }}>确认新增</Button></>}>
          <ConfirmNote>{note}</ConfirmNote>
          <KeyValue items={summary(pending)} />
        </Modal>
      )}
    </>
  );
}
